import React from 'react';
import { colors, isLightColor } from '../../../utils/theme';
import Text from '../Text';
import Stack from '../Stack';
import Icon from '../Icon';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import { StyleProp, ViewStyle } from 'react-native';
import { getSpacing } from '../../../utils/spacing';

export type ChipOwnProps = {
    title: string;
    highlight?: boolean;
    onDelete?: () => void;
    isDisabled?: boolean;
    testID?: string;
    style?: StyleProp<ViewStyle>;
    isSecondaryChip?: boolean;
    customColor?: string;
};

export type ChipProps = ChipOwnProps;

const Chip: React.FC<ChipProps> = (props) => {
    const isDisabledByParent = useIsDisabledByParent(props.isDisabled);
    const getBackgroundColor = () => {
        if (props.customColor) {
            return props.customColor;
        }
        if (props.isSecondaryChip) {
            return colors['Primary-Reg'];
        }

        return props.highlight ? colors['Primary'] : colors['Primary-Light'];
    };

    const getTextColor = (bgColor: string) => {
        return isLightColor(bgColor) ? colors['Dark'] : colors['White'];
    };

    const backgroundColor = getBackgroundColor();

    const content = (
        <Stack
            horizontal
            space={2.5}
            style={[
                {
                    padding: 4,
                    borderRadius: 5,
                    backgroundColor,
                    maxWidth: '100%',
                },
                props.style,
            ]}
            testID={props.testID || 'chip'}
            align="center"
        >
            <Text
                variant="caption-1"
                color={'White-Text'}
                style={{ backgroundColor, color: getTextColor(backgroundColor), maxWidth: '100%' }}
                wrapText
            >
                {props.title}
            </Text>
            {props.onDelete ? <Icon size="checkbox-icon" name="X" color="White-Text" /> : null}
        </Stack>
    );

    if (props.onDelete) {
        return (
            <PressableDynamicOpacity
                isDisabled={isDisabledByParent}
                onPress={props.onDelete}
                style={{ maxWidth: '100%' }}
            >
                {content}
            </PressableDynamicOpacity>
        );
    } else {
        return content;
    }
};

export default React.memo(Chip);

export type ChipSetChip = {
    title: string;
    isMuted?: boolean;
    onDelete?: () => void;
    testID?: string;
    isSecondaryChip?: boolean;
};

export type ChipSetProps = {
    chips: ChipSetChip[];
    style?: StyleProp<ViewStyle>;
};

export const ChipSet: React.FC<ChipSetProps> = ({ chips, style }) => {
    return (
        <Stack horizontal space={2.5} style={[{ flexWrap: 'wrap', marginBottom: -1 * getSpacing(2.5) }, style]}>
            {chips.map((chip, index) => (
                <Chip
                    key={`${chip.title}-${index}`}
                    title={chip.title}
                    highlight={!chip.isMuted}
                    onDelete={chip.onDelete}
                    testID={chip.testID}
                    style={{ marginBottom: getSpacing(2.5) }}
                    isSecondaryChip={chip.isSecondaryChip}
                />
            ))}
        </Stack>
    );
};
